import React, { useContext, useEffect, useState } from 'react';
import {
	Button,
	TextField,
	Typography,
	IconButton,
	Dialog,
	DialogContent,
	Container,
	Box,
	DialogTitle,
	Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { GridColDef, GridToolbar } from '@mui/x-data-grid';
import ProductCard from './ProductCard';
import CreateProducts from './CreateProducts';
import {
	StyledCrossIconButton,
	StyledDataGrid,
} from '../../../components/component/ThemeComponent';
import { deleteProduct, getAllProducts, updateProduct } from '../../../services/api/ProductAPI';
import ViewProducts from './ViewPorducts';
import userContext from '../../../configs/context/context';
import LottieAnimation from '../Vendor/Vendor-ui/lottieAnimation';

interface wholesale {
	type: 'text' | 'image';
	label: string;
	required: boolean;
}

interface buldDiscount {
	minQuantity: number;
	maxQuantity: number;
	discountPercentage: number;
}

interface SizesVariant {
	_id?: string;
	size: string;
}

export interface ColorVariant {
	colorName: string;
	colorCode: string;
	price: number;
	strickoutPrice: number;
	sizes: SizesVariant[];
	colorImage: string[];
	_id?: string;
}

interface Product {
	_id: string;
	name: string;
	category: string;
	description: string;
	price: number;
	quantity: string;
	bestSelling: boolean;
	trending: boolean;
	image: string[];
	productImage: string[];
	weight: string;
	weightUnit: string;
	isInquiry: boolean;
	isBulkDiscount: boolean;
	isCustomized: boolean;
	slug?: string;
	isWholesale: boolean;
	wholesaleFields: wholesale[];
	buldDiscountsData: buldDiscount[];
	wholesaleMinQuantity: number;
	productId: string;
	isStickoutPrice: boolean;
	isColorVariantAvailable: boolean;
	strickoutPrice: number;
	colorVariants: ColorVariant[];
	canonicalTag?: any;
	keywords?: any;
	metaDescription?: any;
	metaTitle?: any;
}

export interface ProductCardProps {
	_id: string;
	name: string;
	price: number;
	stock: number;
	rating: number;
	category: string;
	image: string[];
	description: string;
	bestSelling: boolean;
	trending: boolean;
	quantity: number;
	status: string;
	isInquiry: boolean;
	weight: number;
	weightUnit: string;
	createdBy: string;
	reviews: any[];
	slug: string;
	wholesale: { minOrderQuantity: number };
	isBulkDiscount: boolean;
	isCustomized: boolean;
	isWholesale: boolean;
	wholesaleFields: wholesale[];
	buldDiscountsData: buldDiscount[];
	wholesaleMinQuantity: number;
	productId: string;
	isStickoutPrice: boolean;
	isColorVariantAvailable: boolean;
	strickoutPrice: number;
	colorVariants: ColorVariant[];
	fetchData: () => Promise<void>;
	onClick: () => void;
	handleEditProduct: (product: ProductCardProps) => void;
}

const Product = () => {
	const [products, setProducts] = useState<ProductCardProps[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [isGridView, setIsGridView] = useState(true);
	const [openDialog, setOpenDialog] = useState(false);
	const [currentProduct, setCurrentProduct] = useState<ProductCardProps | null>(null);
	const [displayCount, setDisplayCount] = useState(12);
	const [productCount, setProductCount] = useState();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const { Vendorme } = useContext<any>(userContext);
	const permmission = Vendorme?.permissions?.products;
	const slug = localStorage.getItem('slug');
	const location = window.location.pathname.split('/');

	const handleAddCategory = () => {
		navigate(location.includes(slug as string) ? `/${slug}/create-product` : '/create-product');
	};

	const handleEditProduct = (product: ProductCardProps) => {
		navigate(
			location.includes(slug as string)
				? `/${slug}/product/${product.slug}`
				: `/product/${product.slug}`
		);
	};

	const handleDeleteProduct = async (_id: string) => {
		const confirmToastId = toast(
			<div>
				<p>Are you sure you want to delete this product?</p>
				<button
					onClick={async () => {
						toast.dismiss(confirmToastId);
						const response = await deleteProduct(_id);
						if (response) {
							toast.success('Product deleted successfully');
							fetchData();
						} else {
							toast.error('Failed to delete product');
						}
					}}
					style={{
						marginRight: '10px',
						padding: '5px 10px',
						background: 'red',
						color: 'white',
						border: 'none',
						cursor: 'pointer',
					}}
					type="button"
				>
					Yes
				</button>
				<button
					onClick={() => toast.dismiss(confirmToastId)}
					style={{
						padding: '5px 10px',
						background: 'gray',
						color: 'white',
						border: 'none',
						cursor: 'pointer',
					}}
					type="button"
				>
					No
				</button>
			</div>,
			{ autoClose: false, closeOnClick: false }
		);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setCurrentProduct(null);
	};

	useEffect(() => {
		fetchData();
	}, []);

	const fetchData = async () => {
		try {
			setLoading(true);
			const response = await getAllProducts();
			if (response.success) {
				setProducts(response.data.products.reverse());
				setProductCount(response.data.productCount);
			} else {
				toast.error('Failed to fetch products');
			}
		} catch (e) {
			console.log(e);
		} finally {
			setLoading(false);
		}
	};

	const filteredProducts = products.filter((product) =>
		product.name.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleLoadMore = () => {
		try {
			setDisplayCount((prevCount) => prevCount + 12);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	const handleViewProduct = (product: Product) => {
		setSelectedProduct(product);
		setIsDialogOpen(true);
	};

	const handleCloseDialogView = () => {
		setSelectedProduct(null);
		setIsDialogOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Name',
			width: 250,
			renderCell: (params) => (
				<span style={{ cursor: 'pointer' }} onClick={() => handleViewProduct(params.row)}>
					{params.row.name}
				</span>
			),
		},
		{
			field: 'price',
			headerName: 'Price',
			width: 150,
			align: 'center', // Align the column header to the center
			renderCell: (params) => {
				if (
					params.row.isColorVariantAvailable &&
					Array.isArray(params.row.colorVariants) &&
					params.row.colorVariants.length > 0
				) {
					const prices = params.row.colorVariants
						.map((variant: any) => variant.price)
						.filter((p: any) => p !== undefined && p !== null);
					if (prices.length === 1) return prices[0];
					if (prices.length > 1) return `${Math.min(...prices)} - ${Math.max(...prices)}`;
					return '-';
				}
				return params.row.price === undefined || params.row.price === ''
					? '-'
					: params.row.price;
			},
		},
		{
			field: 'quantity',
			headerName: 'Stock',
			width: 150,
			align: 'center', // Align the column header to the center
			headerAlign: 'center', // Align the column data to the center
			renderCell: (params) =>
				params.row.quantity === undefined || params.row.quantity === ''
					? '-'
					: params.row.quantity,
		},
		{ field: 'category', headerName: 'Category', width: 200 },
		{
			field: 'status',
			headerName: 'Status',
			width: 150,
			align: 'center', // Align the column header to the center
			headerAlign: 'center', // Align the column data to the center
			renderCell: (params) => (
				<Chip
					label={params.row.status === '1' ? 'Active' : 'InActive'}
					sx={{
						ml: 1,
						backgroundColor: params.row.status === '1' ? '#DFF5D8' : '#FFEBEB',
						color: params.row.status === '1' ? '#2C742F' : '#D32F2F',
						fontWeight: 'bold',
						borderRadius: '4px',
					}}
					size="small"
				/>
			),
		},
		{
			field: 'actions',
			headerName: 'Actions',
			width: 150,
			align: 'center', // Align the column header to the center
			headerAlign: 'center', // Align the column data to the center
			renderCell: (params) => (
				<div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
					{params?.row?.createdByModel === 'Vendor' &&
					location?.includes(slug as string) &&
					(permmission?.edit || permmission?.delete) ? (
						<>
							{permmission?.edit && (
								<IconButton
									onClick={() => handleEditProduct(params.row)}
									sx={{ color: '#000', '&:hover': { color: '#000' } }}
								>
									<EditIcon />
								</IconButton>
							)}
							{permmission?.edit && (
								<div className="checkbox-wrapper">
									<label className="toggleButton">
										<input
											type="checkbox"
											checked={params.row.status === '0'}
											onChange={async () => {
												try {
													const newStatus =
														params.row.status === '1' ? '0' : '1';
													const formData = new FormData();
													formData.append('status', newStatus);

													const response = await updateProduct(
														formData,
														// params.row.slug
														params.row._id
													);
													if (response.success) {
														toast.success(
															'Product status updated successfully'
														);
														fetchData();
													} else {
														toast.error(
															'Failed to update product status'
														);
													}
												} catch (error) {
													toast.error(
														'An error occurred while updating status'
													);
												}
											}}
										/>
										<div>
											<svg viewBox="0 0 44 44">
												<path
													transform="translate(-2.000000, -2.000000)"
													d="M14,24 L21,31 L39.7428882,11.5937758 C35.2809627,6.53125861 30.0333333,4 24,4 C12.95,4 4,12.95 4,24 C4,35.05 12.95,44 24,44 C35.05,44 44,35.05 44,24 C44,19.3 42.5809627,15.1645919 39.7428882,11.5937758"
												/>
											</svg>
										</div>
									</label>
								</div>
							)}
							{permmission?.delete && (
								<IconButton
									onClick={() => handleDeleteProduct(params.row._id)}
									sx={{ color: '#BF3131', '&:hover': { color: '#BF3131' } }}
								>
									<DeleteIcon />
								</IconButton>
							)}
						</>
					) : (
						!location.includes(slug as string) && (
							<>
								<IconButton
									onClick={() => handleEditProduct(params.row)}
									sx={{ color: '#000', '&:hover': { color: '#000' } }}
								>
									<EditIcon />
								</IconButton>
								<div className="checkbox-wrapper">
									<label className="toggleButton">
										<input
											type="checkbox"
											checked={params.row.status === '0'}
											onChange={async () => {
												try {
													const newStatus =
														params.row.status === '1' ? '0' : '1';
													const formData = new FormData();
													formData.append('status', newStatus);

													const response = await updateProduct(
														formData,
														params.row._id
													);
													if (response.success) {
														toast.success(
															'Product status updated successfully'
														);
														fetchData();
													} else {
														toast.error(
															'Failed to update product status'
														);
													}
												} catch (error) {
													toast.error(
														'An error occurred while updating status'
													);
												}
											}}
										/>
										<div>
											<svg viewBox="0 0 44 44">
												<path
													transform="translate(-2.000000, -2.000000)"
													d="M14,24 L21,31 L39.7428882,11.5937758 C35.2809627,6.53125861 30.0333333,4 24,4 C12.95,4 4,12.95 4,24 C4,35.05 12.95,44 24,44 C35.05,44 44,35.05 44,24 C44,19.3 42.5809627,15.1645919 39.7428882,11.5937758"
												/>
											</svg>
										</div>
									</label>
								</div>
								<IconButton
									onClick={() => handleDeleteProduct(params.row._id)}
									sx={{ color: '#BF3131', '&:hover': { color: '#BF3131' } }}
								>
									<DeleteIcon />
								</IconButton>
							</>
						)
					)}
				</div>
			),
		},
	];

	return (
		<>
			<Container>
				{!location.includes(slug as string) && (
					<Typography
						gutterBottom
						style={{ fontFamily: 'Poppins', fontWeight: '700', fontSize: '24px' }}
					>
						Products({productCount})
					</Typography>
				)}
				<Typography
					className="d-flex"
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						flexDirection: { xs: 'column', sm: 'row', md: 'row' },
					}}
				>
					<div>
						<span
							style={{
								fontFamily: 'Poppins',
								fontWeight: '400',
								fontSize: '14px',
								color: '#006D48',
							}}
						>
							Manage your product inventory, add new products, and more.
						</span>
					</div>
					<Typography
						sx={{
							width: {
								xs: '100%',
							},
							flexBasis: { xs: 'auto', sm: 'content', md: 'content' },
						}}
					>
						{location?.includes(slug as string) && permmission?.create ? (
							<Button
								sx={{
									width: {
										xs: '100%',
										sm: 'auto',
										md: 'auto',
									},
								}}
								variant="contained"
								color="primary"
								onClick={handleAddCategory}
							>
								Add Product
							</Button>
						) : (
							!location.includes(slug as string) && (
								<Button
									sx={{
										width: {
											xs: '100%',
											sm: 'auto',
											md: 'auto',
										},
									}}
									variant="contained"
									color="primary"
									onClick={handleAddCategory}
								>
									Add Product
								</Button>
							)
						)}
					</Typography>
				</Typography>

				<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
					<Grid size={{ xs: 12, sm: 10, md: 11 }}>
						<TextField
							label="Search Products"
							variant="outlined"
							margin="none"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							size="small"
							fullWidth
							sx={{ marginTop: '10px' }}
						/>
					</Grid>

					<Grid size={{ xs: 12, sm: 2, md: 1 }}>
						<Typography
							sx={{
								display: 'flex',
								justifyContent: { xs: 'start', sm: 'end', md: 'end' },
								alignItems: 'center',
							}}
						>
							<IconButton onClick={() => setIsGridView(true)}>
								<ViewModuleIcon color={isGridView ? 'primary' : 'inherit'} />
							</IconButton>
							<IconButton onClick={() => setIsGridView(false)}>
								<ViewListIcon color={!isGridView ? 'primary' : 'inherit'} />
							</IconButton>
						</Typography>
					</Grid>
				</Grid>

				{!loading && filteredProducts.length === 0 ? (
					<LottieAnimation />
				) : isGridView ? (
					<Grid container spacing={2}>
						{filteredProducts.slice(0, displayCount).map((product) => (
							<Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }} key={product._id}>
								<ProductCard
									fetchData={fetchData}
									product={{
										...product,
										name: product.name,
										description: product.description,
										category: product.category,
										price: product.price,
										quantity: product?.quantity?.toString(),
										bestSelling: product.bestSelling,
										trending: product.trending,
										productImage: [],
										weight: product.weight?.toString(),
										weightUnit: product.weightUnit,
										slug: product.slug,
										isInquiry: product.isInquiry,
										isBulkDiscount: product.isBulkDiscount,
										isCustomized: product?.isCustomized,
										isWholesale: product.isWholesale,
										wholesaleFields: product.wholesaleFields,
										buldDiscountsData: product.buldDiscountsData,
										wholesaleMinQuantity: product.wholesaleMinQuantity,
										productId: product.productId,
										isStickoutPrice: product.isStickoutPrice,
										isColorVariantAvailable: product.isColorVariantAvailable,
										strickoutPrice: product.strickoutPrice,
										colorVariants: product.colorVariants,
									}}
									handleEditProduct={() => handleEditProduct(product)}
									onClick={() => console.log('Product clicked:', product._id)}
								/>
							</Grid>
						))}
					</Grid>
				) : (
					<Box sx={{ height: 600, width: '100%' }}>
						<StyledDataGrid
							rows={filteredProducts}
							columns={columns}
							initialState={{
								pagination: {
									paginationModel: { pageSize: 10 },
								},
							}}
							slots={{ toolbar: GridToolbar }}
							pageSizeOptions={[10]}
							getRowId={(row) => row._id}
						/>
					</Box>
				)}

				{displayCount < filteredProducts.length ? (
					<Button
						onClick={handleLoadMore}
						variant="contained"
						color="primary"
						loading={loading}
						loadingIndicator="Loading…"
					>
						Load More
					</Button>
				) : (
					<Typography
						variant="body1"
						color="text.secondary"
						sx={{
							width: '100%',
							textAlign: 'center',
							fontWeight: 700,
							marginTop: '2rem',
						}}
					>
						Reached full product list
					</Typography>
				)}

				<Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
					<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
						<span>{currentProduct?.slug ? 'Edit Product' : 'Create New Product'}</span>
						<StyledCrossIconButton onClick={handleCloseDialog}>
							<CloseIcon />
						</StyledCrossIconButton>
					</DialogTitle>
					<DialogContent sx={{ paddingTop: '10px !important' }}>
						<CreateProducts
							openDialog={handleCloseDialog}
							fetchData={fetchData}
							product={{
								_id: currentProduct?._id || '',
								name: currentProduct?.name || '',
								description: currentProduct?.description || '',
								category: currentProduct?.category || '',
								price:
									(currentProduct?.price && currentProduct?.price.toString()) ||
									'',
								quantity: currentProduct?.quantity?.toString() || '',
								bestSelling: currentProduct?.bestSelling || false,
								trending: currentProduct?.trending || false,
								productImage: currentProduct?.image || [],
								weight:
									(currentProduct?.weight && currentProduct?.weight.toString()) ||
									'',
								weightUnit: currentProduct?.weightUnit || 'kg',
								slug: currentProduct?.slug || undefined,
								isInquiry: currentProduct?.isInquiry || false,
								isBulkDiscount: currentProduct?.isBulkDiscount || false,
								isCustomized: currentProduct?.isCustomized || false,
								isWholesale: currentProduct?.isWholesale || false,
								wholesaleFields: currentProduct?.wholesaleFields || [],
								buldDiscountsData: currentProduct?.buldDiscountsData || [],
								wholesaleMinQuantity: currentProduct?.wholesaleMinQuantity || 0,
								productId: currentProduct?.productId || '',
							}}
						/>
					</DialogContent>
				</Dialog>
			</Container>

			<Dialog open={isDialogOpen} onClose={handleCloseDialogView} maxWidth="md" fullWidth>
				<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
					<span>View Products</span>
					<StyledCrossIconButton onClick={handleCloseDialogView}>
						<CloseIcon />
					</StyledCrossIconButton>
				</DialogTitle>
				<DialogContent sx={{ paddingTop: '10px !important' }}>
					<ViewProducts product={selectedProduct} />
				</DialogContent>
			</Dialog>
		</>
	);
};

export default Product;
